<template>
  <template v-if="label">
    <slot name="label">
      <SrcInputLabel :for="inputId">{{ label }}</SrcInputLabel>
    </slot>
  </template>
  <div class="w-full" :class="[label ? 'mt-1' : '', error ? 'mb-1' : 'mb-4']">
    <SrcInput
      :id="inputId"
      :value="modelValue"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :focus="focus"
      @input="handleInput"
      @keyup="validate"
      v-bind="$attrs"
      :class="{
        'border-2 border-red-300 bg-red-50 hover:bg-red-50 focus:border-red-300 focus:bg-red-50': error,
      }"
    />
    <p v-if="error" class="mt-1 text-xs text-red-600 text-right">
      {{ error }}
    </p>
  </div>
</template>

<script>
import SrcInput from '../sources/SrcInput.vue';
import SrcInputLabel from '../sources/SrcInputLabel.vue';
import { useI18n } from 'vue-i18n';

export default {
  setup() {
    const { t } = useI18n();
    return { t };
  },
  components: {
    SrcInput,
    SrcInputLabel,
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    focus: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validationType: {
      type: String,
      validator: (value) => [
        'ip',
        'subnet',
        'gateway',
        'dnsserver',
        'decimal',
        'url',
        'email',
        'filled',
        'number',
      ].includes(value),
    },
    validator_off: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputId: `Input-${Math.random().toString(36).substring(7)}`,
      error: '',
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.validate();
      },
    },
    validator_off: {
      immediate: true,
      handler() {
        this.validate();
      },
    },
  },
  methods: {
    handleInput(event) {
      this.$emit('update:modelValue', event.target.value);
      this.validate();
    },
    validate() {
      if (this.validator_off) {
        this.error = '';
        this.$emit('valid', true); // Validation always true when validator_off is true
        return;
      }
      this.error = ''; // Reset error message
      const patterns = {
        ip: /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
        subnet: /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
        gateway: /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
        dnsserver: /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
        decimal: /^[0-9,]+(?:[-'\\S][0-9,]+)*$/,
        email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        number: /^\d+$/,
      };

      const errorMessages = {
        ip: this.$t('setnetwork.novalidip'),
        subnet: this.$t('setnetwork.novalidsubnet'),
        gateway: this.$t('setnetwork.novalidgateway'),
        dnsserver: this.$t('setnetwork.novaliddnsserver'),
        decimal: this.$t('setgeneral.statpriceerror'),
        email: this.$t('setnetwork.noemail'),
        filled: this.$t('setnetwork.notempty'),
        number: this.$t('setnetwork.nonumber'),
      };

      if (this.validationType === 'filled') {
        if (!this.modelValue || this.modelValue.trim() === '') {
          this.error = errorMessages.filled;
          this.$emit('valid', false); // Emit false for failed validation
        } else {
          this.error = '';
          this.$emit('valid', true); // Emit true for successful validation
        }
      } else if (this.validationType === 'dnsserver' && (!this.modelValue || this.modelValue.trim() === '')) {
        this.error = '';
        this.$emit('valid', true); // Emit true for empty optional field
      } else {
        const pattern = patterns[this.validationType];
        if (pattern && !pattern.test(this.modelValue)) {
          this.error = errorMessages[this.validationType];
          this.$emit('valid', false); // Emit false for failed validation
        } else {
          this.error = '';
          this.$emit('valid', true); // Emit true for successful validation
        }
      }
    },
  },
};
</script>

<style scoped>
/* Add any specific styles here if needed */
</style>
